import React, { useState, useRef } from 'react';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';

import { ClientApiService } from 'services/ClientApiService';
import { Button, ButtonVariant, ButtonType, ButtonSize } from 'components/Button';
import { arrowRight } from 'icons/default';
import { loader } from 'icons/misc';
import { getDataLayerEvents } from 'utils/getDatalayerEvents';
import { InputText } from 'components/InputText';
import { InputSelect } from 'components/InputSelect';

import styles from './ReloanCalculator.module.scss';

export const ReloanCalculator: React.FC = () => {
  const scollToRef = useRef<null | HTMLDivElement>(null);

  const phoneRegex = /^([+0]){1}([\d\s]{8,15})$/;
  const licensePlateRegex = /^[A-Za-z]{3}\d{2}[A-Za-z\d]$/;

  const methods = useForm();

  const [isLoading, setLoading] = useState(false);
  const [showContactFrom, setShowContactForm] = useState(true);
  const [success, setSuccess] = useState(false);
  const [generalError, setGeneralError] = useState(false);

  const onSubmit: SubmitHandler<any> = async (data) => {
    const dataToSend = Object.assign(data, {
      current_loan_amount: data.current_loan_amount,
      license_plate: data.license_plate,
      phone_number: data.phone_number,
      email: data.email,
    });

    try {
      setLoading(true);

      const method = 'postReloanWatingList';
      const payload = dataToSend;
      const response = await ClientApiService.proxyRequest({ method, payload });
      // const responseJson = await response.json();
      // console.log(dataToSend);
      if (!response.ok) {
        // console.log(responseJson);
        setLoading(false);
        setShowContactForm(false);
        setGeneralError(true);
        if (scollToRef.current) {
          scollToRef.current.scrollIntoView();
        }
      } else {
        //  console.log(responseJson);
        setLoading(false);
        setShowContactForm(false);
        setSuccess(true);

        getDataLayerEvents('reloan-wating-list');
        if (scollToRef.current) {
          scollToRef.current.scrollIntoView();
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className={styles.wrapper}>
      {!success && !generalError && <h2>Skriv upp dig på väntelistan</h2>}

      {showContactFrom && (
        <div className={styles.contactWrapper}>
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <span className={styles.inputTitle}>Hur mycket har du i billån idag?</span>
              <InputSelect
                name="current_loan_amount"
                options={[
                  '100 000 - 110 000',
                  '120 000 - 130 000',
                  '140 000 - 150 000',
                  '160 000 - 170 000',
                  '180 000 - 190 000',
                  '200 000 - 210 000',
                  '220 000 - 230 000',
                  '240 000 - 250 000',
                  '260 000 - 270 000',
                  '280 000 - 290 000',
                  '300 000 - 310 000',
                  '320 000 - 330 000',
                  '340 000 - 350 000',
                  '360 000 - 370 000',
                  '380 000 - 390 000',
                  '400 000 - 410 000',
                  '420 000 - 430 000',
                  '440 000 - 450 000',
                  '460 000 - 470 000',
                  '480 000 - 490 000',
                  '500 000 - 510 000',
                  '520 000 - 530 000',
                  '540 000 - 550 000',
                  '560 000 - 570 000',
                  '580 000 - 590 000',
                  '600 000 - 610 000',
                  '620 000 - 630 000',
                  '640 000 - 650 000',
                  '660 000 - 670 000',
                  '680 000 - 690 000',
                  '700 000 - 710 000',
                  '720 000 - 730 000',
                  '740 000 - 750 000',
                  '760 000 - 770 000',
                  '780 000 - 790 000',
                  '800 000 - 810 000',
                  '820 000 - 830 000',
                  '840 000 - 850 000',
                  '860 000 - 870 000',
                  '880 000 - 890 000',
                  '900 000 - 910 000',
                  '920 000 - 930 000',
                  '940 000 - 950 000',
                  '960 000 - 970 000',
                  '980 000 - 990 000',
                ]}
                size="small"
                registerOptions={{
                  required: 'Ange nuvarande lånebelopp',
                }}
              />
              <span className={styles.inputTitle}>Registreringsnummer</span>
              <InputText
                registerOptions={{
                  required: 'Du måste ange ett giltigt registreringsnummer',
                  pattern: {
                    value: licensePlateRegex,
                    message: 'Du måste ange ett giltigt registreringsnummer',
                  },
                  minLength: {
                    value: 6,
                    message: 'Ett registreringsnummer måste vara 6 tecken långt',
                  },
                }}
                size="medium"
                label="ABC123"
                name="license_plate"
                type="text"
                options={{ maxlength: 6 }}
                uppercase
              />

              <span className={styles.inputTitle}>Telefonnummer</span>
              <InputText
                registerOptions={{
                  required: 'Du måste ange ett giltigt telefonnummer',
                  pattern: {
                    value: phoneRegex,
                    message: 'Du måste ange ett giltigt telefonnummer',
                  },
                }}
                size="medium"
                label="Ditt telefonnummer"
                name="phone_number"
                type="tel"
              />
              <span className={styles.inputTitle}>E-postadress</span>
              <InputText
                registerOptions={{
                  required: 'Du måste ange en giltig e-postadress',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                    message: 'Du måste ange en giltig e-postadress',
                  },
                }}
                size="medium"
                label="namn@email.se"
                name="email"
                type="email"
              />
              <div className={styles.separator} />
              <Button
                type={ButtonType.Submit}
                variant={ButtonVariant.PrimaryMintSweet}
                stretch
                size={ButtonSize.Large}
                disabled={isLoading}
                trailingIcon={isLoading ? loader : arrowRight}
              >
                <span>Skicka</span>
              </Button>
            </form>
          </FormProvider>
        </div>
      )}

      {!isLoading && success && (
        <div className={styles.successWrapper} ref={scollToRef}>
          <h2>Tack!</h2>
          <p>Du kommer löpande få mer information om vår nya tjänst Re:loan</p>
        </div>
      )}
      {generalError && (
        <div className={styles.errorWrapper} ref={scollToRef}>
          <h2>Någonting gick fel!</h2>
          <p>Försök igen eller kontakt oss via chatten.</p>
        </div>
      )}
      <div className={styles.trustpilot}>
        <span>Utmärkt</span>
        <img width="120" src="/assets/icons/logo/Trustpilot_rating.svg" alt="Trustpilot logo" />
        <a href="https://se.trustpilot.com/review/blipp.se" target="_BLANK" rel="noreferrer">
          <img
            width="100"
            src="/assets/icons/logo/Trustpilot_brandmark.svg"
            alt="Trustpilot logo"
          />
        </a>
      </div>
    </div>
  );
};
